"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Settings2 } from "lucide-react";
import { setCustomIndicatorComputeCallback } from "@/lib/custom-indicators/compiled-template";
import { reloadPreviewIndicator } from "@/lib/custom-indicators/reload-preview-indicator";
import { useIndicatorCompileStore } from "@/lib/custom-indicators/runtime/compile-store";
import {
  compileInputsToParsedInputs,
  parseInputs,
  parseIndicatorMeta,
} from "@/lib/custom-indicators/indicator-source";
import {
  clearIndicatorSyncState,
  forceChartIndicatorRecalc,
  type ChartIndicatorApi,
  type IndicatorSyncStateMap,
} from "@/lib/chart-helpers/chart-indicators";
import {
  ChartPanelProvider,
  useChartPanelId,
} from "@/components/chart/ChartPanelContext";
import KLineChartSymbolSelector from "@/components/chart/symbol-selector/KLineChartSymbolSelector";
import { Button } from "@/shadcn-components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components/ui/popover";
import { cn } from "@/lib/utils";
import CustomIndicatorInputsForm, {
  mergeInputValuesWithParsed,
} from "./CustomIndicatorInputsForm";
import { usePersistedPopoverHeight } from "./usePersistedPopoverHeight";
import { CUSTOM_INDICATORS_STORAGE_KEYS } from "@/lib/localStorageKeys/custom-indicators";
import { generateChartStyles } from "@/lib/chart-helpers";
import { useChartSettingsStore } from "@/lib/chart-settings";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import type { TimeFrame } from "@/vybe-trader-client/chart/types";
import type { OHLCData } from "@/vybe-trader-client/chart/types";

/** Matches `sideOffset` on the inputs `PopoverContent`. */
const INPUTS_POPOVER_SIDE_OFFSET = 6;

const ALL_TIMEFRAMES: TimeFrame[] = [
  "1m",
  "5m",
  "15m",
  "30m",
  "1h",
  "2h",
  "4h",
  "12h",
  "1d",
  "1w",
  "1mn",
];

import { klinecharts } from "@/lib/chart-helpers/klinecharts-client";

const LoadDataType = {
  Init: "init",
  Forward: "forward",
  Backward: "backward",
} as const;

function toKLineBars(data: OHLCData[]) {
  return data.map((bar) => ({
    timestamp: bar.timestamp,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume,
  }));
}

function CustomIndicatorPreviewChartInner({
  projectId,
  code = "",
  indicatorName,
}: {
  projectId?: string;
  code?: string;
  indicatorName?: string;
}) {
  const chartPanelId = useChartPanelId();
  const previewRootRef = useRef<HTMLDivElement>(null);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<NonNullable<typeof klinecharts>["init"]> | null>(
    null
  );
  const indicatorSyncRef = useRef<IndicatorSyncStateMap>(new Map());
  const [computeTick, setComputeTick] = useState(0);
  const [dataEmpty, setDataEmpty] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const compileResult = useIndicatorCompileStore((s) =>
    projectId ? s.byProjectId[projectId] : undefined
  );
  const compileRevision = useIndicatorCompileStore((s) =>
    projectId ? (s.revisionByProjectId[projectId] ?? 0) : 0
  );

  const getMaxHeightPx = useCallback(() => {
    const root = previewRootRef.current;
    const trigger = settingsTriggerRef.current;
    if (!root || !trigger) return 0;
    const rootRect = root.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const popoverTop = triggerRect.bottom + INPUTS_POPOVER_SIDE_OFFSET;
    return rootRect.bottom - popoverTop;
  }, []);

  const { heightPx, resizeHandleProps } = usePersistedPopoverHeight({
    storageKey: CUSTOM_INDICATORS_STORAGE_KEYS.EDITOR_INPUTS_PANEL_HEIGHT_PX,
    getMaxHeightPx,
    observeBoundsRef: previewRootRef,
  });
  const [draftInputValues, setDraftInputValues] = useState<
    Record<string, number | string>
  >({});
  const [appliedInputValues, setAppliedInputValues] = useState<
    Record<string, number | string>
  >({});
  // Reset on each symbol/timeframe change so scroll-back can resume fetching.
  const atHistoryEndRef = useRef(false);

  const parsedInputs = useMemo(() => {
    if (compileResult?.success && compileResult.inputs?.length) {
      return compileInputsToParsedInputs(compileResult.inputs);
    }
    return parseInputs(code);
  }, [code, compileResult]);
  const inputNamesKey = useMemo(
    () => parsedInputs.map((i) => i.name).join("\0"),
    [parsedInputs]
  );

  useEffect(() => {
    setAppliedInputValues((prev) =>
      mergeInputValuesWithParsed(prev, parsedInputs)
    );
    setDraftInputValues((prev) =>
      mergeInputValuesWithParsed(prev, parsedInputs)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when input schema changes
  }, [inputNamesKey]);

  const appliedValuesKey = useMemo(
    () => JSON.stringify(appliedInputValues),
    [appliedInputValues]
  );

  useEffect(() => {
    if (!settingsOpen) return;
    setDraftInputValues({ ...appliedInputValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync draft only when popover opens
  }, [settingsOpen]);

  const displayName =
    compileResult?.name?.trim() ||
    indicatorName?.trim() ||
    parseIndicatorMeta(code).name ||
    "Custom indicator";

  useEffect(() => {
    setCustomIndicatorComputeCallback(() => setComputeTick((t) => t + 1));
    return () => setCustomIndicatorComputeCallback(null);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    void reloadPreviewIndicator({
      chart: chartRef.current as ChartIndicatorApi | null,
      syncState: indicatorSyncRef.current,
      projectId,
      displayName,
      compileResult,
      appliedInputValues,
      hasChartData: !dataEmpty,
    });
  }, [
    projectId,
    compileRevision,
    compileResult?.success,
    displayName,
    appliedValuesKey,
    dataEmpty,
  ]);

  useEffect(() => {
    forceChartIndicatorRecalc(chartRef.current as ChartIndicatorApi | null);
  }, [computeTick]);

  const handleSaveInputs = useCallback(() => {
    if (!projectId) return;
    setAppliedInputValues({ ...draftInputValues });
  }, [projectId, draftInputValues]);

  const selectedSymbol = useChartStore3(
    (s) => s.slots[chartPanelId].selectedSymbol
  );
  const selectedTimeframe = useChartStore3(
    (s) => s.slots[chartPanelId].selectedTimeframe
  );
  const ohlcRevision = useChartStore3((s) => s.slots[chartPanelId].ohlcRevision);

  const chartType = useChartSettingsStore(
    (state) => state.chartSettings.chartType
  );
  const chartStyle = useChartSettingsStore(
    (state) => state.chartSettings.chartStyle
  );

  const chartStyles = useMemo(
    () => generateChartStyles(chartStyle, chartType, undefined, false),
    [chartStyle, chartType]
  );

  useEffect(() => {
    if (!containerRef.current || !klinecharts) return;

    const chart = klinecharts.init(containerRef.current);
    if (!chart) return;

    chartRef.current = chart;
    chart.setStyles(chartStyles as never);
    if (selectedSymbol) {
      chart.setPriceVolumePrecision(selectedSymbol.digits, 2);
    }
    chart.resize();

    let scrollLoadInflight = false;

    (chart as unknown as {
      setLoadDataCallback: (cb: (params: {
        type: string;
        data?: { timestamp: number } | null;
        callback: (data: unknown[], more?: boolean) => void;
      }) => void) => void;
    }).setLoadDataCallback((params) => {
      if (params.type !== LoadDataType.Forward) {
        params.callback([], params.type === LoadDataType.Forward);
        return;
      }
      const timestamp = params?.data?.timestamp;
      if (timestamp == null || atHistoryEndRef.current) {
        params.callback([], !atHistoryEndRef.current);
        return;
      }
      if (scrollLoadInflight) {
        params.callback([], true);
        return;
      }
      scrollLoadInflight = true;
      useChartStore3
        .getState()
        .getMoreData(timestamp, chartPanelId)
        .then((olderData) => {
          scrollLoadInflight = false;
          if (chartRef.current !== chart) return;
          const hasMore = Array.isArray(olderData) && olderData.length > 0;
          if (!hasMore) atHistoryEndRef.current = true;
          params.callback(
            hasMore ? (toKLineBars(olderData as OHLCData[]) as unknown[]) : [],
            hasMore
          );
        })
        .catch((error) => {
          scrollLoadInflight = false;
          const reason =
            error && typeof error.message === "string" ? error.message : "";
          if (reason === "Symbol changed" || reason === "Timeframe changed") {
            return;
          }
          params.callback([], false);
        });
    });

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      clearIndicatorSyncState(indicatorSyncRef.current);
      if (containerRef.current && klinecharts) {
        klinecharts.dispose(containerRef.current);
      }
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once; styles/symbol updated in other effects
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setStyles(chartStyles as never);
  }, [chartStyles]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !selectedSymbol) return;
    chart.setPriceVolumePrecision(selectedSymbol.digits, 2);
  }, [selectedSymbol]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !selectedSymbol) {
      setDataEmpty(true);
      return;
    }

    let cancelled = false;
    // New symbol/timeframe: allow scroll-back to fetch history again.
    atHistoryEndRef.current = false;

    const load = async () => {
      try {
        const data = await useChartStore3
          .getState()
          .getInitialData(chartPanelId);
        if (cancelled || chartRef.current !== chart) return;
        if (data.length === 0) {
          setDataEmpty(true);
          return;
        }
        setDataEmpty(false);
        chart.applyNewData(toKLineBars(data) as never[]);
        chart.resize();
      } catch {
        if (!cancelled) setDataEmpty(true);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [
    chartPanelId,
    selectedSymbol?.currency,
    selectedTimeframe,
    ohlcRevision,
  ]);

  const handleTimeframeSelect = (timeframe: TimeFrame) => {
    useChartStore3.getState().setSelectedTimeframe(timeframe, chartPanelId);
  };

  return (
    <div
      ref={previewRootRef}
      className="flex h-full min-h-0 flex-col bg-background"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-1.5">
        <KLineChartSymbolSelector isDesktop />
        <div className="h-3 w-px shrink-0 bg-border" />
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {ALL_TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => handleTimeframeSelect(tf)}
              className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors ${
                selectedTimeframe === tf
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-foreground hover:bg-muted"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        <div className="h-3 w-px shrink-0 bg-border" />
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={settingsTriggerRef}
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 shrink-0 transition-colors",
                settingsOpen &&
                  "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
              )}
              title={
                settingsOpen ? "Hide indicator inputs" : "Show indicator inputs"
              }
              aria-label="Toggle indicator inputs"
              aria-pressed={settingsOpen}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex w-[340px] flex-col overflow-hidden p-0"
            style={{ height: heightPx }}
            align="end"
            side="bottom"
            sideOffset={INPUTS_POPOVER_SIDE_OFFSET}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <div className="shrink-0 border-b border-border px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                Inputs
              </p>
              <p className="text-sm font-medium truncate">{displayName}</p>
            </div>
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3"
              onWheel={(e) => e.stopPropagation()}
            >
              <CustomIndicatorInputsForm
                parsedInputs={parsedInputs}
                values={draftInputValues}
                onValueChange={(n, v) =>
                  setDraftInputValues((prev) => ({ ...prev, [n]: v }))
                }
              />
            </div>
            <div className="shrink-0 border-t border-border px-3 py-2">
              <Button
                type="button"
                className="w-full"
                size="sm"
                disabled={!compileResult?.success}
                onClick={handleSaveInputs}
              >
                Save
              </Button>
            </div>
            <div {...resizeHandleProps}>
              <div className="z-10 h-0.5 w-8 rounded-sm bg-border" />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative min-h-0 flex-1 w-full">
        <div ref={containerRef} className="h-full w-full" />
        {dataEmpty ? (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            No chart data loaded for the current symbol
          </p>
        ) : !compileResult?.success ? (
          <p className="pointer-events-none absolute bottom-2 left-0 right-0 text-center text-xs text-muted-foreground">
            Compile the project to plot the indicator on this chart
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function CustomIndicatorPreviewChart({
  panelId,
  projectId,
  code,
  indicatorName,
}: {
  panelId?: ChartPanelId;
  projectId?: string;
  code?: string;
  indicatorName?: string;
}) {
  const ctxPanel = useChartPanelId();
  const effectivePanel = panelId ?? ctxPanel;
  return (
    <ChartPanelProvider panelId={effectivePanel}>
      <CustomIndicatorPreviewChartInner
        projectId={projectId}
        code={code}
        indicatorName={indicatorName}
      />
    </ChartPanelProvider>
  );
}
